import { useCallback, useEffect, useState } from 'react';
import { AppLayout, Button, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import { useApiClient } from '../hooks/useApiClient.js';

/**
 * Shape of an invitation returned by `GET /account/invitations`.
 */
interface Invitation {
  id: string;
  eventId: string;
  eventName: string;
  eventDate: string;
  status: 'pending' | 'accepted' | 'rejected';
}

/**
 * Invitations page — displays pending event invitations for the participant
 * with Accept/Reject actions.
 *
 * Requirements:
 *   - 22.1: display a list of pending event invitations
 *   - 22.2: allow the participant to accept an invitation
 *   - 22.3: allow the participant to reject an invitation
 *   - 14.1: accept/reject invitations
 */
export function InvitationsPage(): JSX.Element {
  const api = useApiClient();

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const fetchInvitations = useCallback(
    async (client: ApiClient): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        const data = await client.request<{ invitations: Invitation[] }>('GET', '/account/invitations');
        setInvitations(data.invitations);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load invitations',
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    void fetchInvitations(api).then(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [api, fetchInvitations]);

  async function handleAccept(invitationId: string): Promise<void> {
    try {
      setActionInProgress(invitationId);
      setError(null);
      await api.acceptInvitation(invitationId);
      // Update local state to reflect acceptance
      setInvitations((prev) =>
        prev.map((inv) =>
          inv.id === invitationId ? { ...inv, status: 'accepted' as const } : inv,
        ),
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to accept invitation',
      );
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleReject(invitationId: string): Promise<void> {
    try {
      setActionInProgress(invitationId);
      setError(null);
      await api.rejectInvitation(invitationId);
      // Update local state to reflect rejection
      setInvitations((prev) =>
        prev.map((inv) =>
          inv.id === invitationId ? { ...inv, status: 'rejected' as const } : inv,
        ),
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to reject invitation',
      );
    } finally {
      setActionInProgress(null);
    }
  }

  const pendingInvitations = invitations.filter((inv) => inv.status === 'pending');
  const resolvedInvitations = invitations.filter((inv) => inv.status !== 'pending');

  return (
    <AppLayout title="Invitations">
      {error && (
        <ErrorBanner onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Spinner label="Loading invitations…" size="1.5rem" />
        </div>
      )}

      {!loading && pendingInvitations.length === 0 && resolvedInvitations.length === 0 && (
        <p style={{ color: 'var(--color-text-secondary, #6B7280)', textAlign: 'center', padding: '2rem 0' }}>
          No invitations found.
        </p>
      )}

      {!loading && pendingInvitations.length > 0 && (
        <section aria-label="Pending invitations">
          <h2 style={{ fontSize: 'var(--font-size-heading-sm, 1.25rem)', marginBottom: '0.75rem' }}>
            Pending Invitations
          </h2>
          <ul
            style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.75rem' }}
            aria-label="Pending invitation list"
          >
            {pendingInvitations.map((inv) => (
              <li
                key={inv.id}
                style={{
                  border: '1px solid var(--color-border, #E5E7EB)',
                  borderRadius: 'var(--radius-card, 12px)',
                  padding: '1rem',
                }}
              >
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong style={{ fontSize: 'var(--font-size-body-md, 1rem)' }}>{inv.eventName}</strong>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--font-size-body-sm, 0.875rem)',
                      color: 'var(--color-text-secondary, #6B7280)',
                      marginTop: '0.25rem',
                    }}
                  >
                    Date: {inv.eventDate}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    variant="primary"
                    disabled={actionInProgress === inv.id}
                    onClick={() => void handleAccept(inv.id)}
                    aria-label={`Accept invitation to ${inv.eventName}`}
                  >
                    {actionInProgress === inv.id ? 'Processing…' : 'Accept'}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={actionInProgress === inv.id}
                    onClick={() => void handleReject(inv.id)}
                    aria-label={`Reject invitation to ${inv.eventName}`}
                  >
                    {actionInProgress === inv.id ? 'Processing…' : 'Reject'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!loading && resolvedInvitations.length > 0 && (
        <section aria-label="Resolved invitations" style={{ marginTop: '1.5rem' }}>
          <h2 style={{ fontSize: 'var(--font-size-heading-sm, 1.25rem)', marginBottom: '0.75rem' }}>
            Past Invitations
          </h2>
          <ul
            style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.5rem' }}
            aria-label="Resolved invitation list"
          >
            {resolvedInvitations.map((inv) => (
              <li
                key={inv.id}
                style={{
                  border: '1px solid var(--color-border, #E5E7EB)',
                  borderRadius: 'var(--radius-card, 12px)',
                  padding: '0.75rem 1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  opacity: 0.7,
                }}
              >
                <div>
                  <strong>{inv.eventName}</strong>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--font-size-body-sm, 0.875rem)',
                      color: 'var(--color-text-secondary, #6B7280)',
                    }}
                  >
                    {inv.eventDate}
                  </span>
                </div>
                <span
                  style={{
                    padding: '0.125rem 0.5rem',
                    borderRadius: 'var(--radius-button, 8px)',
                    fontSize: 'var(--font-size-body-sm, 0.875rem)',
                    fontWeight: 500,
                    backgroundColor: inv.status === 'accepted' ? '#e6f4ea' : '#fef2f2',
                    color: inv.status === 'accepted' ? 'var(--color-brand-green, #1B4332)' : 'var(--color-brand-red, #DC2F02)',
                  }}
                >
                  {inv.status}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </AppLayout>
  );
}
